import { LocalStorageKeys } from "../constants";
import { getCheckoutLink } from "../services/apiV2";
import { axiosErrorMatches } from "../helpers/error";
import { useAnalytics } from "./useAnalytics";
import { TPricingPlan } from "../features/pricingPlans/pricingPlans.types";
import { getUserThunk } from "../features/user/user.thunk";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { useNavigate } from "react-router-dom";
import { selectExtension } from "../features/extension/extension.selector";
import { selectPromos, selectUserProfile } from "../features/user/user.selector";
import { useCallback } from "react";
import {
  getPaymentStoreDisplayName,
  isStoreManagedSubscription,
} from "../helpers";

export const useCheckout = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { trackBeginCheckout } = useAnalytics();
  const { isExtension } = useAppSelector(selectExtension);
  const { growthAnnualOneTimePromo } = useAppSelector(selectPromos);
  const user = useAppSelector(selectUserProfile);

  const checkout = useCallback(
    async ({
      plan,
      // redirectUrl: defaultRedirectUrl,
      couponId,
      redirectUrl,
      growthAnnualParams = "",
    }: {
      plan: TPricingPlan;
      redirectUrl: string;
      couponId?: string;
      growthAnnualParams?: string;
    }) => {
      // const growthAnnualOneTimePromoAvailable =
      //   growthAnnualOneTimePromo &&
      //   !(plan.name === "growth" && plan.period === "annually");
      // const redirectUrl = growthAnnualOneTimePromoAvailable
      //   ? `/annual-one-time-offer?from=${isExtension ? "extension" : "website"}${growthAnnualParams ? `&${growthAnnualParams}` : ""}`
      //   : defaultRedirectUrl;

      if (user && isStoreManagedSubscription(user.store)) {
        const storeName = getPaymentStoreDisplayName(user.store);
        throw new Error(
          `You subscribed to this plan on ${storeName}. Please use ${storeName} to change your plan.`,
        );
      }

      try {
        const res = await getCheckoutLink({
          planId: plan.planId,
          couponId: couponId,
          redirectUrl,
        });

        if (res.status === 200) {
          if (!res.data.checkoutLink) {
            await dispatch(getUserThunk());
            const status =
              res.data.changeType === "DowngradeScheduled"
                ? "downgrade"
                : "upgrade";
            navigate(
              redirectUrl.includes("?")
                ? `${redirectUrl}&status=${status}`
                : `${redirectUrl}?status=${status}`,
            );
          } else {
            if (plan) {
              trackBeginCheckout({
                planId: plan.id,
                planName: plan.name,
                planPrice: plan.price,
                planDuration: plan.period || undefined,
              });
            }
            window.open(
              res.data.checkoutLink,
              isExtension ? "_blank" : "_self",
            );
          }
        }
      } catch (e) {
        if (axiosErrorMatches(e, `User already has the same plan ${plan.id}`)) {
          throw new Error("User already has this plan");
        } else {
          throw new Error("Failed to update plan");
        }
      }
    },
    [isExtension, user],
  );

  return {
    checkout,
    growthAnnualOneTimePromo,
    trackBeginCheckout,
  };
};

export default useCheckout;
